/**
 * Donation CTA layout variants (NOS-152). `banner` remains DonationCTASection
 * (untouched default). Variants share the block's content fields and
 * deleted/hidden flags — switching never loses content — and are token-driven
 * via the --nos-* variables. Both variants open the same DonationModal flow as
 * the default section.
 *
 * Note: element_order/_el_styles are banner-layout features; variants define
 * their own composition and intentionally ignore them (same convention as
 * hero variants).
 */
import { useState } from "react"
import { BadgeCheck, Shield } from "lucide-react"
import { Button } from "@/components/ui/button"
import { DonationModal } from "@/components/DonationModal"
import type { OrgCustomizations, Organization } from "@/templates/types"
import type { BlockComponentProps } from "./shared"

function parseCtaContent(content: Record<string, unknown> | null) {
  const secondaryDeleted = Boolean(content?.secondary_cta_text_deleted || content?.secondary_cta_text_hidden)
  return {
    heading: (content?.heading as string) ?? "Support our mission",
    body:
      (content?.body as string) ??
      "Your donation makes a difference. Every gift helps us continue our work in the community.",
    ctaText: (content?.cta_text as string) ?? "Donate now",
    headingDeleted: Boolean(content?.heading_deleted || content?.heading_hidden),
    bodyDeleted: Boolean(content?.body_deleted || content?.body_hidden),
    ctaDeleted: Boolean(content?.cta_text_deleted || content?.cta_text_hidden),
    // NOS-236: optional secondary CTA (paired source buttons, e.g. Volunteer)
    secondaryText: secondaryDeleted ? "" : (((content?.secondary_cta_text as string) ?? "").trim()),
    secondaryUrl: ((content?.secondary_cta_url as string) ?? "").trim() || "#",
  }
}

/** NOS-236: quiet secondary action under/beside the donate button. */
function SecondaryCta({ text, url }: { text: string; url: string }) {
  return (
    <Button
      size="lg"
      variant="outline"
      className="min-h-[48px] w-full px-8 font-semibold transition-all hover:scale-105 @sm:min-h-[52px] @sm:text-lg"
      style={{
        backgroundColor: "transparent",
        color: "var(--nos-color-primary, #3b82f6)",
        borderWidth: 2,
        borderStyle: "solid",
        borderColor: "var(--nos-color-primary, #3b82f6)",
        borderRadius: "var(--nos-button-radius, 8px)",
      }}
      asChild
    >
      <a href={url}>{text}</a>
    </Button>
  )
}

/** Accent falls back to secondary (resolveTokens defaults accent = secondary), so orgs without an accent keep the primary→secondary gradient. */
const GRADIENT = "linear-gradient(135deg, var(--nos-color-primary, #3b82f6) 0%, var(--nos-color-accent, var(--nos-color-secondary, #1e40af)) 100%)"

function DonateButton({
  text,
  disabled,
  onClick,
  className = "",
}: {
  text: string
  disabled: boolean
  onClick: () => void
  className?: string
}) {
  return (
    <Button
      size="lg"
      className={`min-h-[48px] w-full px-8 font-semibold shadow-lg transition-all hover:scale-105 hover:opacity-90 @sm:min-h-[52px] @sm:text-lg ${className}`.trim()}
      style={{
        backgroundColor: "var(--nos-button-bg, var(--nos-color-primary, #3b82f6))",
        color: "var(--nos-button-color, var(--nos-color-text-on-primary, #ffffff))",
        borderWidth: 2,
        borderStyle: "solid",
        borderColor: "var(--nos-button-border-color, transparent)",
        borderRadius: "var(--nos-button-radius, 8px)",
      }}
      onClick={onClick}
      disabled={disabled}
    >
      {text}
    </Button>
  )
}

function TrustBadges() {
  return (
    <div
      className="mt-6 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-sm"
      style={{ color: "var(--nos-color-text-muted, #64748b)" }}
    >
      <span className="flex items-center gap-2">
        <BadgeCheck className="h-4 w-4" strokeWidth={2} />
        Tax-deductible
      </span>
      <span className="flex items-center gap-2">
        <Shield className="h-4 w-4" strokeWidth={2} />
        Secure donation
      </span>
    </div>
  )
}

function CtaDonationModal({
  isOpen,
  onClose,
  org,
  customizations,
}: {
  isOpen: boolean
  onClose: () => void
  org?: Organization | null
  customizations: OrgCustomizations
}) {
  if (!org) return null
  return (
    <DonationModal
      isOpen={isOpen}
      onClose={onClose}
      orgId={org.id}
      orgName={org.name}
      primaryColor={customizations?.primary_color} // hex required: feeds Stripe Elements appearance, which cannot resolve CSS vars
    />
  )
}

/** Split panel: copy on the left, donate card on the right. */
export function DonationCtaSplitPanel({ content, customizations, org }: BlockComponentProps) {
  const [isDonationModalOpen, setIsDonationModalOpen] = useState(false)
  const c = parseCtaContent(content)
  return (
    <section
      id="donate"
      className="px-4 @sm:px-6 @md:px-8"
      style={{
        paddingTop: "var(--nos-space-section-y, 64px)",
        paddingBottom: "var(--nos-space-section-y, 64px)",
      }}
    >
      <div
        className="mx-auto grid max-w-5xl gap-8 p-8 @md:grid-cols-2 @md:items-center @md:gap-12 @md:p-12"
        style={{
          background: "var(--nos-color-surface-alt, #f1f5f9)",
          borderRadius: "var(--nos-radius, 12px)",
        }}
      >
        <div>
          {!c.headingDeleted && (
            <h2 className="text-3xl font-bold tracking-tight" style={{ color: "var(--nos-color-text, #0f172a)" }}>
              {c.heading}
            </h2>
          )}
          {!c.bodyDeleted && (
            <p className="mt-4 text-lg leading-relaxed" style={{ color: "var(--nos-color-text-muted, #64748b)" }}>
              {c.body}
            </p>
          )}
        </div>
        <div
          className="p-6 @sm:p-8"
          style={{
            background: "var(--nos-color-surface, #ffffff)",
            boxShadow: "var(--nos-elevation-shadow, none)",
            borderRadius: "var(--nos-radius, 12px)",
          }}
        >
          {!c.ctaDeleted && (
            <DonateButton text={c.ctaText} disabled={!org} onClick={() => setIsDonationModalOpen(true)} />
          )}
          {c.secondaryText && (
            <div className="mt-3">
              <SecondaryCta text={c.secondaryText} url={c.secondaryUrl} />
            </div>
          )}
          <TrustBadges />
        </div>
      </div>
      <CtaDonationModal
        isOpen={isDonationModalOpen}
        onClose={() => setIsDonationModalOpen(false)}
        org={org}
        customizations={customizations}
      />
    </section>
  )
}

/** Floating card: gradient band with a centered elevated donate card. */
export function DonationCtaFloatingCard({ content, customizations, org }: BlockComponentProps) {
  const [isDonationModalOpen, setIsDonationModalOpen] = useState(false)
  const c = parseCtaContent(content)
  return (
    <section
      id="donate"
      className="px-4 @sm:px-6 @md:px-8"
      style={{
        background: GRADIENT,
        paddingTop: "calc(var(--nos-space-section-y, 64px) * 1.25)",
        paddingBottom: "calc(var(--nos-space-section-y, 64px) * 1.25)",
      }}
    >
      <div
        className="mx-auto max-w-md p-8 text-center"
        style={{
          background: "var(--nos-color-surface, #ffffff)",
          boxShadow: "var(--nos-elevation-shadow, none)",
          borderRadius: "var(--nos-radius, 12px)",
        }}
      >
        {!c.headingDeleted && (
          <h2 className="text-2xl font-bold tracking-tight" style={{ color: "var(--nos-color-text, #0f172a)" }}>
            {c.heading}
          </h2>
        )}
        {!c.bodyDeleted && (
          <p className="mt-3 text-sm leading-relaxed" style={{ color: "var(--nos-color-text-muted, #64748b)" }}>
            {c.body}
          </p>
        )}
        {!c.ctaDeleted && (
          <DonateButton
            text={c.ctaText}
            disabled={!org}
            onClick={() => setIsDonationModalOpen(true)}
            className="mt-6"
          />
        )}
        {c.secondaryText && (
          <div className="mt-3">
            <SecondaryCta text={c.secondaryText} url={c.secondaryUrl} />
          </div>
        )}
      </div>
      <CtaDonationModal
        isOpen={isDonationModalOpen}
        onClose={() => setIsDonationModalOpen(false)}
        org={org}
        customizations={customizations}
      />
    </section>
  )
}
